<template>
  <div class="admin-number-detail bg-white-custom border px-2 py-4">
    <div
      v-if="isLoading"
      class="d-flex justify-content-center align-items-center loading-container"
    >
      <div class="spinner-border text-center mt-3" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <div v-else>
      <b-container fluid>
        <b-row>
          <b-col md="6">
            <b-form-group label="Number:" label-for="number-input">
              <b-form-input
                id="number-input"
                v-model="form.number"
                :disabled="true"
                placeholder="(443) 788-1866"
              ></b-form-input>
            </b-form-group>
            <b-form-group label="Label:" label-for="label-input">
              <b-form-input
                id="label-input"
                v-model="form.label"
                :disabled="!editCapability"
                :placeholder="prettyPhoneNumber(form.number)"
              ></b-form-input>
            </b-form-group>
            <b-form-group label="Capabilities:">
              <b-form-tags
                input-id="capabilities"
                tag-class="number-tags"
                v-model="form.capabilities"
                add-on-enter
                :disabled="!editCapability"
              ></b-form-tags>
            </b-form-group>
            <b-form-group label="Reply Message:" label-for="reply-message">
              <b-form-textarea
                id="reply-message"
                v-model="form.reply_message"
                placeholder="Sample message here."
                :disabled="!editCapability"
              ></b-form-textarea>
            </b-form-group>
            <b-form-group label="Help Message:" label-for="help-message">
              <b-form-textarea
                id="help-message"
                v-model="form.help_message"
                placeholder="..."
                :disabled="!editCapability"
              ></b-form-textarea>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            class="d-flex flex-column justify-content-between align-items-start"
          >
            <b-form-group
              label="Status:"
              label-for="status-select"
              class="w-100"
            >
              <b-form-select
                id="status-select"
                class="form-select-field"
                v-model="form.status"
                :disabled="!editCapability"
                :options="statusOptions"
              ></b-form-select>
            </b-form-group>
            <b-form-group
              label="ISO Code:"
              label-for="iso-code-select"
              class="w-100"
            >
              <b-form-select
                id="iso-code-select"
                class="form-select-field"
                v-model="form.country_code"
                :disabled="!editCapability"
                :options="countryCodeOptions"
              ></b-form-select>
            </b-form-group>
            <b-form-group
              label="Provider:"
              label-for="provider-select"
              class="w-100"
            >
              <b-form-select
                id="provider-select"
                class="form-select-field"
                :disabled="!editCapability"
                v-model="form.provider"
                :options="providerOptions"
              ></b-form-select>
            </b-form-group>
            <div v-if="editCapability" class="save-button-container w-100">
              <b-button variant="success" pill @click="saveNumberDetails">Save</b-button>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import formatter from "@/mixins/formatter";
import errorHandler from "@/mixins/errorHandler";

export default {
  name: "AdminNumberDetail",
  mixins: [formatter, errorHandler],
  props: { numberId: String },
  data() {
    return {
      isLoading: false,
      form: {
        number: "",
        label: "",
        capabilities: [],
        reply_message: "",
        help_message: "",
        status: null,
        country_code: null,
        provider: null,
      },
      statusOptions: [
        { value: null, text: "Please select an option", disabled: true },
        { value: "active", text: "Active" },
        { value: "inactive", text: "Inactive" },
      ],
      countryCodeOptions: [{ value: "us", text: "US" }],
      providerOptions: [
        { value: null, text: "Please select a provider", disabled: true },
        { value: "aculab", text: "Aculab" },
        { value: "twilio", text: "Twilio" },
        { value: "comtech", text: "Comtech" },
      ],
      fetchAbortController: null,
    };
  },
  computed: {
    ...mapGetters("user", ["currentToken"]),
    ...mapGetters("agency", ["currentAgencyId", "currentAgency"]),
    editCapability() {
      return this.$permission('update:number');
    }
  },
  created() {
    this.fetchNumberDetails();
  },
  methods: {
    async fetchNumberDetails() {
      try {
        this.fetchAbortController = new AbortController();
        this.isLoading = true;
        const response = await this.$ConveyApi.numbersApi.getProviderNumber({
          numberOrId: this.numberId,
          signal: this.fetchAbortController.signal,
        });
        const rawNumberDetails = response.data.data;
        const {
          capabilities,
          number,
          status,
          provider,
          help_message,
          country_code,
          reply_message,
          label,
        } = rawNumberDetails;
        this.form = {
          number,
          status,
          provider,
          help_message,
          country_code,
          reply_message,
          label,
          capabilities: typeof capabilities === "string" ? capabilities.split(",") : capabilities,
        };
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[AdminNumberDetail:fetchNumberDetails] ERROR: ", err);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async saveNumberDetails() {
      try {
        this.isLoading = true;
        const {
          capabilities,
          number,
          status,
          provider,
          help_message,
          country_code,
          reply_message,
          label,
        } = this.form;
        const payload = {
          capabilities: capabilities.join(","),
          number,
          status,
          provider,
          help_message,
          country_code,
          reply_message,
          friendly_name: label,
        };
        await this.$ConveyApi.numbersApi.updateProviderNumber({
          numberOrId: this.numberId,
          payload,
        });
        this.$toastr("success", "Provider number has been updated.");
      } catch(err) {
        console.log("[saveNumberDetails] ERROR: ", err);
        this.$toastr("error", "Error encountered updating provider number.");
      } finally {
        this.isLoading = false;
      }
    },
  },
  beforeDestroy() {
    if (this.fetchAbortController) {
      this.fetchAbortController.abort();
      this.fetchAbortController = null;
    }
  }
};
</script>

<style lang="scss">
.admin-number-detail {
  max-width: 1024px;
  border-radius: 1rem;
}

.loading-container {
  height: 450px;
}

.form-select-field {
  background-size: 12px;
  font-size: 14px;
}

.save-button-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  align-items: flex-end;
  justify-content: flex-end;
}

.number-tags {
  background-color: #b7f1e0 !important;
  color: #00a173 !important;
}
</style>
